// Questionnaire Screen - Duolingo-Style Single Question UI
import { useState, useEffect } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS } from '../config/constants';
import { CORE_QUESTIONS, convertToServiceFormat } from '../config/core-questions';
import { Container, Text, Button, IconButton, ProgressBar } from '../components/ui';
import Icons from '../components/Icons';

function QuestionnaireScreen() {
    const { setCurrentScreen, goBack, setQuestionnaireAnswers, capturedImages } = useApp();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [direction, setDirection] = useState('next'); // 'next' or 'prev'

    const totalQuestions = CORE_QUESTIONS.length;
    const currentQuestion = CORE_QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / totalQuestions) * 100;
    const hasImages = Object.keys(capturedImages).length > 0;

    // Handle answer selection with auto-advance
    const handleAnswer = (value) => {
        if (isTransitioning) return;

        // Save answer
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));

        // Auto-advance after short delay
        if (currentIndex < totalQuestions - 1) {
            setIsTransitioning(true);
            setDirection('next');

            setTimeout(() => {
                setCurrentIndex(currentIndex + 1);
                setIsTransitioning(false);
            }, 300);
        }
    };

    // Navigate to previous question
    const handlePrev = () => {
        if (currentIndex > 0 && !isTransitioning) {
            setIsTransitioning(true);
            setDirection('prev');

            setTimeout(() => {
                setCurrentIndex(currentIndex - 1);
                setIsTransitioning(false);
            }, 200);
        }
    };

    // Skip current question
    const handleSkip = () => {
        if (currentIndex < totalQuestions - 1) {
            setDirection('next');
            setCurrentIndex(currentIndex + 1);
        } else {
            handleComplete();
        }
    };

    // Complete questionnaire
    const handleComplete = () => {
        const analysisData = convertToServiceFormat(answers);
        setQuestionnaireAnswers(analysisData);
        setCurrentScreen(SCREENS.ANALYSIS);
    };

    const isLastQuestion = currentIndex === totalQuestions - 1;
    const currentAnswer = answers[currentQuestion.id];

    return (
        <Container className="min-h-screen bg-theme-bg flex flex-col" safe>
            {/* Header */}
            <div className="header-fullwidth border-b border-theme">
                <div className="header-content">
                    <IconButton onClick={currentIndex > 0 ? handlePrev : goBack}>
                        <Icons.Back />
                    </IconButton>
                    <div className="flex-1 mx-3 text-center">
                        <Text className="font-semibold text-sm sm:text-base block">
                            Câu {currentIndex + 1}/{totalQuestions}
                        </Text>
                        {hasImages && (
                            <Text variant="small" className="text-success mt-0.5">✓ Đã chụp ảnh</Text>
                        )}
                    </div>
                    <button
                        onClick={handleSkip}
                        className="text-primary text-sm font-semibold touch-target"
                    >
                        Bỏ qua
                    </button>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="px-responsive pt-4">
                <ProgressBar progress={progress} />
            </div>

            {/* Question Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-responsive py-8">
                <div
                    className={`w-full max-w-md text-center transition-all duration-300 ${isTransitioning
                            ? direction === 'next'
                                ? 'opacity-0 translate-x-8'
                                : 'opacity-0 -translate-x-8'
                            : 'opacity-100 translate-x-0'
                        }`}
                >
                    {/* Icon */}
                    <div className="text-5xl mb-6 animate-bounce-subtle">
                        {currentQuestion.icon}
                    </div>

                    {/* Question Text */}
                    <Text variant="h3" className="mb-8 px-4 leading-relaxed">
                        {currentQuestion.text}
                    </Text>

                    {/* Answer Options */}
                    <div className="space-y-3">
                        {currentQuestion.options.map((option, idx) => (
                            <button
                                key={option.value}
                                onClick={() => handleAnswer(option.value)}
                                disabled={isTransitioning}
                                className={`
                                    w-full py-4 px-6 rounded-2xl text-base font-medium
                                    transition-all duration-200 transform
                                    ${currentAnswer === option.value
                                        ? 'bg-primary text-white scale-[1.02] shadow-lg'
                                        : 'bg-theme-secondary text-theme-primary hover:bg-theme-tertiary hover:scale-[1.01]'
                                    }
                                    ${isTransitioning ? 'pointer-events-none' : 'active:scale-[0.98]'}
                                `}
                                style={{
                                    animationDelay: `${idx * 50}ms`,
                                    animation: !isTransitioning ? 'fadeInUp 0.3s ease-out forwards' : 'none'
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer - Only show complete button on last question */}
            {isLastQuestion && currentAnswer && (
                <div className="bottom-nav-wrapper bg-theme-bg border-t border-theme">
                    <div className="bottom-nav-inner !border-t-0 p-4">
                        <Button onClick={handleComplete} className="w-full">
                            Phân tích ngay 🚀
                        </Button>
                    </div>
                </div>
            )}

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 pb-6">
                {CORE_QUESTIONS.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => !isTransitioning && setCurrentIndex(idx)}
                        className={`
                            h-2 rounded-full transition-all duration-300
                            ${idx === currentIndex
                                ? 'w-6 bg-primary'
                                : answers[CORE_QUESTIONS[idx].id]
                                    ? 'w-2 bg-success'
                                    : 'w-2 bg-theme-tertiary'
                            }
                        `}
                    />
                ))}
            </div>
        </Container>
    );
}

export default QuestionnaireScreen;
